import { useState } from 'react';
import { Table } from 'antd';
import type { TableProps } from 'antd';
import moment from 'moment';
import HeartLoader from '@/shared/ui/HeartLoader';
import type { UploadRow, UploadsListResult } from '@/models/upload';
import AssignPatientModal from '@/features/uploads/AssignPatientModal';
import { displayMrn, displayPatientName } from '@/shared/utils/displayPatient';
import UploadStatusBadge from './UploadStatusBadge';
import UploadRowActions from './UploadRowActions';
import styles from './UploadsTable.module.css';

interface UploadsTableProps {
  data?: UploadsListResult;
  loading?: boolean;
  page: number;
  pageSize: number;
  onTableChange: TableProps<UploadRow>['onChange'];
}

const STUDY_TYPE_LABEL: Record<string, string> = {
  TTE: 'Echo – TTE',
  ULTRASOUND: 'Ultrasound',
  STRESS_ECHO: 'Stress Echo',
};

const formatStudyType = (modality: string | null) =>
  (modality && STUDY_TYPE_LABEL[modality]) || modality || '—';

// Until the upload has a study there is no patient: show the file the user
// picked instead (R9).
const patientLine = (row: UploadRow) =>
  row.studyUuid
    ? displayPatientName(
        [row.patientFirstName, row.patientLastName].filter(Boolean).join(' ')
      )
    : row.filename;

const mrnLine = (row: UploadRow) =>
  row.studyUuid ? displayMrn(row.patientMedicalRecordNumber) : '—';

const formatRelative = (iso: string | null) => {
  if (!iso) return '—';
  const m = moment(iso);
  if (!m.isValid()) return '—';
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'day').startOf('day');
  if (m.isSameOrAfter(today)) return `Today, ${m.format('h:mm A')}`;
  if (m.isSameOrAfter(yesterday)) return `Yesterday, ${m.format('h:mm A')}`;
  return m.format('MMM D, YYYY');
};

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const m = moment(iso);
  return m.isValid() ? m.format('MMM D, YYYY') : '—';
};

export default function UploadsTable({
  data,
  loading,
  page,
  pageSize,
  onTableChange,
}: UploadsTableProps) {
  const [patientModalRow, setPatientModalRow] = useState<UploadRow | null>(
    null
  );
  const columns: TableProps<UploadRow>['columns'] = [
    {
      title: 'Patient',
      key: 'patient',
      render: (_: unknown, record: UploadRow) => (
        <div className={styles.patientCell}>
          <span className={styles.patientName}>{patientLine(record)}</span>
          <span className={styles.mrnLine}>MRN: {mrnLine(record)}</span>
        </div>
      ),
    },
    {
      title: 'Study Type',
      dataIndex: 'modality',
      key: 'modality',
      render: (value: string | null) => formatStudyType(value),
    },
    {
      title: 'Study Date',
      dataIndex: 'studyDate',
      key: 'studyDate',
      render: (value: string | null) => formatDate(value),
    },
    {
      title: 'Uploaded',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) => formatRelative(value),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (_: unknown, record: UploadRow) => (
        <UploadStatusBadge upload={record} />
      ),
    },
    {
      title: 'Site / Source',
      dataIndex: 'site',
      key: 'site',
      render: (value: string | null) => value || '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_: unknown, record: UploadRow) => (
        <UploadRowActions
          upload={record}
          onPatientClick={() => setPatientModalRow(record)}
        />
      ),
    },
  ];

  return (
    <>
      <Table<UploadRow>
        columns={columns}
        dataSource={data?.results}
        rowKey={(r) => r.uuid}
        loading={{ spinning: !!loading, indicator: <HeartLoader size="sm" /> }}
        onChange={onTableChange}
        bordered={false}
        pagination={{
          current: page,
          pageSize,
          total: data?.count,
          showSizeChanger: true,
          showTotal: (t, [s, e]) => `${s}-${e} of ${t}`,
        }}
      />
      <AssignPatientModal
        open={patientModalRow !== null}
        upload={patientModalRow}
        onClose={() => setPatientModalRow(null)}
      />
    </>
  );
}
